import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Bug, Send, Mail, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUG_CATEGORIES = [
  { value: 'ui', label: 'User Interface Issue' },
  { value: 'functionality', label: 'Feature Not Working' },
  { value: 'performance', label: 'Slow Performance' },
  { value: 'crash', label: 'App Crash/Error' },
  { value: 'data', label: 'Data Loss/Corruption' },
  { value: 'security', label: 'Security Concern' },
  { value: 'suggestion', label: 'Feature Suggestion' },
  { value: 'other', label: 'Other' }
];

const SEVERITY_LEVELS = [
  { value: 'low', label: 'Low - Minor inconvenience', color: 'bg-blue-500' },
  { value: 'medium', label: 'Medium - Affects usability', color: 'bg-amber-500' },
  { value: 'high', label: 'High - Major functionality broken', color: 'bg-orange-500' },
  { value: 'critical', label: 'Critical - Cannot use app', color: 'bg-red-500' }
];

export default function BugReportDialog({ trigger }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState({
    category: '',
    severity: 'medium',
    title: '',
    description: '',
    steps_to_reproduce: '',
    expected_behavior: '',
    actual_behavior: '',
    browser_info: navigator.userAgent,
    screen_size: `${window.innerWidth}x${window.innerHeight}`,
    contact_email: user?.email || '',
    allow_contact: true
  });

  const handleSubmit = async () => {
    if (!report.title || !report.description || !report.category) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/support/bug-report`, {
        ...report,
        user_id: user?.user_id,
        user_email: user?.email,
        user_name: user?.name,
        submitted_at: new Date().toISOString(),
        url: window.location.href,
        app_version: '1.0.0-beta'
      });

      toast.success(
        <div>
          <p className="font-medium">Bug report submitted!</p>
          <p className="text-sm text-muted-foreground">Thank you for helping us improve SlideCon.</p>
        </div>
      );

      setOpen(false);
      setReport({
        category: '',
        severity: 'medium',
        title: '',
        description: '',
        steps_to_reproduce: '',
        expected_behavior: '',
        actual_behavior: '',
        browser_info: navigator.userAgent,
        screen_size: `${window.innerWidth}x${window.innerHeight}`,
        contact_email: user?.email || '',
        allow_contact: true
      });
    } catch (error) {
      // Even if API fails, offer email fallback
      toast.error(
        <div>
          <p>Failed to submit report</p>
          <p className="text-sm">Please email us directly at cs@myrdkllc.com</p>
        </div>
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="gap-2">
            <Bug className="w-4 h-4" />
            Report Bug
          </Button>
        )}
      </DialogTrigger>
      
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="w-5 h-5 text-red-500" />
            Report a Bug or Issue
            <Badge variant="outline" className="text-amber-500 border-amber-500 ml-2">BETA</Badge>
          </DialogTitle>
          <DialogDescription>
            Help us improve SlideCon by reporting bugs, issues, or suggestions. 
            Your feedback is valuable during this beta period.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Category & Severity */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category <span className="text-red-500">*</span></Label>
              <Select value={report.category} onValueChange={(v) => setReport({ ...report, category: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {BUG_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Severity</Label>
              <Select value={report.severity} onValueChange={(v) => setReport({ ...report, severity: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITY_LEVELS.map(sev => (
                    <SelectItem key={sev.value} value={sev.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${sev.color}`} />
                        {sev.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label>Title <span className="text-red-500">*</span></Label>
            <Input 
              placeholder="Brief summary of the issue"
              value={report.title}
              onChange={(e) => setReport({ ...report, title: e.target.value })}
              data-testid="bug-report-title"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>Description <span className="text-red-500">*</span></Label>
            <Textarea 
              placeholder="Describe the issue in detail. What were you trying to do? What happened?"
              value={report.description}
              onChange={(e) => setReport({ ...report, description: e.target.value })}
              rows={3}
              data-testid="bug-report-description"
            />
          </div>

          {/* Steps to Reproduce */}
          <div className="space-y-2">
            <Label>Steps to Reproduce (optional)</Label>
            <Textarea 
              placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
              value={report.steps_to_reproduce}
              onChange={(e) => setReport({ ...report, steps_to_reproduce: e.target.value })}
              rows={3}
            />
          </div>

          {/* Expected vs Actual */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Expected Behavior</Label>
              <Textarea 
                placeholder="What should have happened?"
                value={report.expected_behavior}
                onChange={(e) => setReport({ ...report, expected_behavior: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Actual Behavior</Label>
              <Textarea 
                placeholder="What actually happened?"
                value={report.actual_behavior}
                onChange={(e) => setReport({ ...report, actual_behavior: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-3 pt-2 border-t">
            <div className="flex items-start gap-3">
              <Checkbox 
                id="allow_contact" 
                checked={report.allow_contact}
                onCheckedChange={(checked) => setReport({ ...report, allow_contact: checked === true })}
              />
              <div>
                <label htmlFor="allow_contact" className="text-sm font-medium cursor-pointer">
                  Allow us to contact you about this report
                </label>
                <p className="text-xs text-muted-foreground">
                  We may need additional information to resolve the issue
                </p>
              </div>
            </div>
            
            {report.allow_contact && (
              <div className="space-y-2">
                <Label>Contact Email</Label>
                <Input 
                  type="email"
                  placeholder="your@email.com"
                  value={report.contact_email}
                  onChange={(e) => setReport({ ...report, contact_email: e.target.value })}
                />
              </div>
            )}
          </div>

          {/* System Info */}
          <div className="p-3 bg-muted/50 rounded-lg text-xs text-muted-foreground space-y-1">
            <p><strong>System Info (auto-collected):</strong></p>
            <p>Screen: {report.screen_size}</p>
            <p>Browser: {report.browser_info.substring(0, 80)}...</p>
            <p>Page: {window.location.pathname}</p>
          </div>

          {/* Direct Email Option */}
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-300 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              You can also email us directly at{' '}
              <a href="mailto:cs@myrdkllc.com" className="font-medium underline">cs@myrdkllc.com</a>
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading} data-testid="submit-bug-report">
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit Report
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
